import React, { forwardRef, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useRecoilState } from 'recoil';

import { fullscreenVODPageTestIDs, pictureDetailedVODPageTestIDs, Theme } from '@constants';
import { navigationService } from '@services';
import { vodPlaylistState } from '@store';

import { TimeUtils } from '../../../helpers/TimeUtils';
import { FocusableFlatList } from '../../focusable/FocusableFlatList/FocusableFlatList';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { OnDemandContinueWatchingTile } from '../../tiles/OnDemandCOntinueWatchingTile/OnDemandContinueWatchingTile';
import { OnDemandLongFormItemTile } from '../../tiles/OnDemandLongFormItemTile/OnDemandLongFormItemTile';
import { OnDemandShortFormItemTile } from '../../tiles/OnDemandShortFormItemTile/OnDemandShortFormItemTile';

const T_PATHS = {
  shortform: 'shortFormItemTile',
  longform: 'longFormItemTile',
  continueWatching: 'continueWatchingTile',
};

export const OnDemandRow = memo(
  forwardRef(({ sizes, rowData, rowIndex, nextFocusUp, nextFocusDown, onFocus }, reference) => {
    const rowType = rowData.category.custom_fields.find(filed => filed.field_title).field_value;
    const playlist = rowType === 'shortform' ? rowData.channels[0].playlist : rowData.channels; // longform
    const rowTitle = rowData.category.name;
    const tileSizes = sizes[rowType];
    const tPath = T_PATHS[rowType];
    const tileWidth = tileSizes.TILE_WIDTH;
    const spacing = tileSizes.TILE_SPACING;

    const [currentVODPlaylistState, setVODPLaylist] = useRecoilState(vodPlaylistState);

    const { t } = useTranslation('translation', {
      keyPrefix: tPath,
    });

    const _getItemLayout = useCallback(
      (_, index) => ({
        length: tileWidth,
        offset: (tileWidth + spacing) * index,
        index,
      }),
      [tileWidth, spacing],
    );

    const _renderItem = useCallback((data, index) => {
      const safeDuration = data?.duration ?? 0;
      let duration = rowType === 'continueWatching' ? safeDuration - data?.point ?? 0 : safeDuration;

      if (duration < 0) {
        duration = 0;
      }
      const min = TimeUtils.getMinutes(duration);
      const sec = TimeUtils.getSeconds(duration);
      const tileProperties = {
        accessibilityLabel: t('accessibilityVoiceover', {
          index: index + 1,
          length: playlist.length,
          rowTitle,
          title: data.title,
          minutes: min,
          seconds: sec,
          episode: data.episode,
          season: data.channel?.season ?? 1,
          series: data.seriestitle,
        }),
        accessibilityHint: t('accessibilityHint'),
        data,
        testID: `list_${rowIndex}_${index + 1}`,
        onFocus,
        onPress: () => {
          switch (rowType) {
            case 'shortform': {
              setVODPLaylist({
                ...currentVODPlaylistState,
                playlist,
                title: rowTitle,
                currentID: data._id,
              });
              navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
                videoID: data._id,
                playlist,
                playlistEpisodeIndex: index,
                data,
              });
              break;
            }
            case 'longform': {
              navigationService.navigate(pictureDetailedVODPageTestIDs.pictureDetailedVODPage, {
                showID: data._id,
                playlist,
                data,
              });
              break;
            }
            case 'continueWatching': {
              navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
                videoID: data._id,
                seasonID: data.channel?._id,
                needsBookmarking: true,
                startPosition: data.point,
              });
              break;
            }
          }
        },
      };

      switch (rowType) {
        case 'shortform': {
          return <OnDemandShortFormItemTile {...tileProperties} />;
        }
        case 'longform': {
          return <OnDemandLongFormItemTile {...tileProperties} />;
        }
        case 'continueWatching': {
          return <OnDemandContinueWatchingTile {...tileProperties} />;
        }
        default:
          return null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const _keyExtractor = useCallback((_, index) => `list_${rowIndex}_${index + 1}`, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const _separator = useCallback(({ leadingItem }) => <View style={{ width: spacing }} />, []);

    return (
      <View style={styles.wrapper}>
        <TWCText
          accessibilityHint={t('accessibilityHint')}
          accessibilityLabel={t('accessibilityLabel', {
            rowTitle,
          })}
          style={{
            marginBottom: sizes.rowTitleSpacing,
            marginLeft: sizes.rowOffset,
            height: sizes.rowTitleHeight,
          }}
          testID={`vodlist_${rowType}_${rowIndex}_title`}
          variant={'mediumText36'}
        >
          {rowTitle}
        </TWCText>
        <FocusableFlatList
          containerStyle
          ItemSeparatorComponent={_separator}
          ListFooterComponent={View}
          ListFooterComponentStyle={{
            width: Theme.px(1920 - tileWidth),
          }}
          data={playlist}
          focusPositions={1}
          getItemLayout={_getItemLayout}
          horizontal
          initialScrollIndex={0}
          itemUnderlayColor={'clear'}
          keyExtractor={_keyExtractor}
          nestedScrollEnabled
          nextFocusDown={nextFocusDown}
          nextFocusLeft={null}
          nextFocusRight={null}
          nextFocusUp={nextFocusUp}
          paddingStart={sizes.rowOffset}
          ref={reference}
          renderItem={_renderItem}
          shouldUseContextReference={false}
          showsHorizontalScrollIndicator={false}
          testID={`vodlist_${rowType}_${rowIndex}`}
          wrapItem={false}
          onFocus={onFocus}
        />
      </View>
    );
  }),
);

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'column',
    width: Theme.appWidth,
  },
});
